import { lazy, StrictMode, Suspense, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { Route, Router } from 'react-router-dom'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import * as Sentry from '@sentry/react'
import LanguageProvider from './containers/language-provider'

import GlobalStyle from './globalStyle'
import { CompatRouter } from 'react-router-dom-v5-compat'
import history from './utils/history'
import './global.css'
import FlagProvider from '@unleash/proxy-client-react'
import { IntercomProvider } from 'react-use-intercom'
import { SnackbarContextProvider } from './components/stories/molecules/snackbar/component/snackbar-context'
import { QueryClientProvider } from '@tanstack/react-query'
import { customQueryClient } from './react-query/config/query-client'
import { useAuthStore } from './zustand/authentication/use-auth-store'
import { initializeApp } from './containers/authentication/utils/initialize-app'
import sentryInit from './utils/instrument'
import App from './containers/app'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { store } from './redux-store'
import PageView from './gtm/page-view.jsx'

const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/build/modern/production.js').then(
    (d) => ({
      default: d.ReactQueryDevtools
    })
  )
)

const { VITE_INTERCOM_APP_ID } = import.meta.env
const { VITE_UNLEASH_BASE_URL, VITE_UNLEASH_CLIENT_KEY } = import.meta.env

const unleashConfig = {
  url: `${VITE_UNLEASH_BASE_URL}/api/frontend`,
  clientKey: VITE_UNLEASH_CLIENT_KEY,
  refreshInterval: 15,
  appName: 'web-app'
}

initializeApp(store).catch((err) => {
  console.error('Failed to initialize app:', err)
})

sentryInit()

const AppRoot = () => {
  const logout = useAuthStore.getState().logout
  const [showDevtools, setShowDevtools] = useState(false)

  useEffect(() => {
    // @ts-ignore
    window.toggleDevtools = () => setShowDevtools((old) => !old)
  }, [])

  useEffect(() => {
    const handleStorageEvent = (event) => {
      if (event.key === 'docuToolsLogout') {
        logout()
      }
    }

    window.addEventListener('storage', handleStorageEvent, false)

    return () => {
      window.removeEventListener('storage', handleStorageEvent)
    }
  }, [])

  const SentryRoute = Sentry.withSentryRouting(Route)

  return (
    <>
      <QueryClientProvider client={customQueryClient}>
        <Provider store={store}>
          <LanguageProvider>
            <DndProvider backend={HTML5Backend}>
              <SnackbarContextProvider>
                <IntercomProvider
                  apiBase="https://api-iam.eu.intercom.io"
                  appId={VITE_INTERCOM_APP_ID}
                  autoBoot
                >
                  <Router history={history}>
                    <CompatRouter>
                      <PageView />
                      <FlagProvider config={unleashConfig}>
                        <StrictMode>
                          <SentryRoute path="*" component={App} />
                        </StrictMode>
                      </FlagProvider>
                    </CompatRouter>
                  </Router>
                </IntercomProvider>
              </SnackbarContextProvider>
            </DndProvider>
          </LanguageProvider>
        </Provider>
        <ReactQueryDevtools initialIsOpen />
        {showDevtools && (
          <Suspense fallback={null}>
            <ReactQueryDevtoolsProduction />
          </Suspense>
        )}
      </QueryClientProvider>
      <GlobalStyle />
    </>
  )
}

createRoot(document.getElementById('root')).render(<AppRoot />)
